import AWS from "aws-sdk";
import imageCompression from "browser-image-compression";
import { enqueueSnackbar } from "notistack";

export function retry(fn, retriesLeft = 5, interval = 1000) {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            // reject('maximum retries exceeded');
            reject(error);
            return;
          }

          // Passing on "reject" is the important part
          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
}

export const bytesToMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);

export const capitalizeFirstLetter = (text) => {
  if (!text) return "N/A";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

let uploadInstance = null;

export const S3Upload = (file, token, identityId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const region = process.env.REACT_APP_REGION;

      if (!region || !identityId || !token) {
        throw new Error(
          "AWS credentials are missing. Please check your parameters."
        );
      }

      AWS.config.update({ region });

      const credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
        IdentityId: identityId,
        Logins: { "cognito-identity.amazonaws.com": token },
      });

      await credentials.getPromise();

      const S3Client = new AWS.S3({ credentials });

      const date = new Date();
      const fileType = file.type || "";

      let folderPath = "images";
      let fileExtension = ".jpg";
      let contentType = "image/jpeg";

      if (fileType.startsWith("video/")) {
        folderPath = "videos";
        // Extract extension from file name or use default extension based on MIME type
        fileExtension = file.name ? `.${file.name.split(".").pop()}` : ".mp4";
        contentType = fileType;
      }

      const params = {
        Bucket: process.env.REACT_APP_BUCKET_NAME,
        Key: `${
          process.env.REACT_APP_S3_MODE
        }/${folderPath}/FILE_${date.getTime()}${fileExtension}`,
        Body: file,
        ContentType: contentType,
      };

      uploadInstance = S3Client.upload(params, function (err, data) {
        if (err) {
          uploadInstance = null;
          console.error("Error uploading file:", err);
          reject(err);
          return;
        }
        try {
          uploadInstance = null;
          resolve({ ...data, name: file.name });
        } catch (error) {
          console.error("Error saving file path:", error);
          reject(error);
        }
      });
    } catch (error) {
      console.error("S3Upload configuration error:", error);
      reject(error);
    }
  });
};

export const CompressImage = async (file) => {
  const options = {
    maxSizeMB: 2,
    useWebWorker: true,
    fileType: "image/jpeg",
  };
  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    console.error("Error compressing image:", error);
    enqueueSnackbar("Error compressing image", { variant: "error" });
    return null;
  }
};

export const S3Delete = (path, token, identityId) => {
  return new Promise((resolve, reject) => {
    if (!path) {
      reject(new Error("No path provided"));
      return;
    }

    AWS.config.update({
      region: process.env.NEXT_PUBLIC_REGION,
      credentials: new AWS.CognitoIdentityCredentials({
        IdentityPoolId: process.env.NEXT_PUBLIC_IDENTITY_POOL_ID,
        IdentityId: identityId,
        Logins: {
          "cognito-identity.amazonaws.com": token,
        },
      }),
    });

    const S3Client = new AWS.S3();

    const params = {
      Bucket: process.env.NEXT_PUBLIC_BUCKET_NAME,
      Key: `${
        process.env.NEXT_PUBLIC_S3_MODE +
        path?.split(process.env.NEXT_PUBLIC_S3_MODE)[1]
      }`,
    };

    S3Client.deleteObject(params, function (err, data) {
      if (err) {
        console.error("Error deleting file:", err);
        reject(err);
        return;
      }
      resolve(data);
    });
  });
};

export const S3Abort = () => {
  if (uploadInstance) {
    try {
      uploadInstance.abort();
      uploadInstance = null;
    } catch (error) {
      console.error("Error aborting upload:", error);
    }
  }
};
